import { dateToLabel, numberToCurrencyString } from 'further-ui/utils';
import { FeeCalculationBasis, FeeType } from 'further-types/investment';
import PageContainer from 'components/PageContainer';
import Table, { TableActions } from 'components/Table';
import {
  useDeleteOneOffFeesAndDiscounts,
  useGetOneOffFeesAndDiscounts,
} from 'hooks/data/fees/useOneOffFeesAndDiscounts';
import { Delete } from '@mui/icons-material';
import { FEE_DISCOUNT_ADD } from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import SearchField from 'components/SearchField';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type OneOffFeesBatch = ReturnType<
  typeof useGetOneOffFeesAndDiscounts
  //@ts-expect-error
>['data']['results'][number];

function getTypeLabel(batch: OneOffFeesBatch) {
  if (!batch.feeType) {
    return 'Bulk upload';
  }

  return `${batch.feeType === FeeType.Fee ? 'Fee' : 'Discount'} ${
    batch.feeCalculationBasis ===
    FeeCalculationBasis.PercentageOfTotalCommitment
      ? '%'
      : '£'
  }`;
}

const OneOffFeesAndDiscounts: React.FC = () => {
  const deleteDisclosure = useDisclosure<{ oneOffFeesBatchId: string }>();
  const [canCreate, canDelete] = useHasPermissions([
    RolePermission.CreateOneOffFeesAndDiscounts,
    RolePermission.DeleteOneOffFeesAndDiscounts,
  ]);

  const pagination = usePagination({
    id: 'one-off-fees-and-discounts',
  });
  const {
    handleChangePage,
    handleChangeRowsPerPage,
    toFirstPage,
    page,
    rowsPerPage,
  } = pagination;

  const { filters, handleChangeFilters } = useFilters('one-off-fee-filter', {
    defaultFilters: {
      searchTerm: '',
    },
    onFiltersChange: toFirstPage,
  });

  const oneOffFeesAndDiscounts = useGetOneOffFeesAndDiscounts({
    search: filters.searchTerm,
    page,
    rowsPerPage,
  });
  const deleteOneOffFeesBatch = useDeleteOneOffFeesAndDiscounts();

  const handleConfirmDelete = () => {
    deleteOneOffFeesBatch.mutate(
      deleteDisclosure.actionProps?.oneOffFeesBatchId,
    );
    deleteDisclosure.onClose();
  };

  const columns = [
    {
      label: 'Fee name',
      key: 'name',
      sort: false,
    },
    {
      label: 'Date',
      key: 'date',
      sort: false,
      render: (row: OneOffFeesBatch) => dateToLabel(row.date),
    },
    {
      label: 'Type',
      key: 'type',
      sort: false,
      render: (row: OneOffFeesBatch) => getTypeLabel(row),
    },
    {
      label: 'Total fee/discount',
      key: 'totalAmount',
      sort: false,
      render: (row: OneOffFeesBatch) => numberToCurrencyString(row.totalAmount),
    },
    {
      label: 'Tranche(s)',
      key: 'fundNames',
      sort: false,
      render: (row: OneOffFeesBatch) =>
        row.fundNames.length > 2
          ? `${row.fundNames.slice(0, 2).join(', ')} and ${
              row.fundNames.length - 2
            } more`
          : row.fundNames.length > 0
          ? row.fundNames.join(', ')
          : 'Investor balance fee',
    },
    {
      label: '# investors',
      key: 'investorIds',
      sort: false,
      render: (row: OneOffFeesBatch) => row.investorIds.length,
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (row: OneOffFeesBatch) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              visible: canDelete,
              onClick: () => {
                deleteDisclosure.stageAction({ oneOffFeesBatchId: row._id });
              },
              disabled: deleteOneOffFeesBatch.isPending,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer heading="Fees: One off fees / discounts">
      <PageContent>
        <PageFilters
          pageId="one-off-fees-and-discounts"
          buttons={[
            {
              link: FEE_DISCOUNT_ADD,
              label: 'Add fee or discount',
              visible: canCreate,
            },
          ]}
        >
          <Filter>
            <SearchField
              name="searchTerm"
              placeholder="Search by fee name or tranche"
              fullWidth
              allowEmptyQuery
              autoSearchOnDebounce
              value={filters.searchTerm}
              onSearch={(searchTerm) => handleChangeFilters({ searchTerm })}
            />
          </Filter>
        </PageFilters>

        <Table
          loading={oneOffFeesAndDiscounts.isFetching}
          columns={columns}
          tablebody={oneOffFeesAndDiscounts.data?.results ?? []}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          count={oneOffFeesAndDiscounts.data?.total ?? 0}
          pagination={true}
          variant="nohover"
          emptyMessage={'No on-off fees / discounts found'}
        />
        <AlertDialog
          onClose={deleteDisclosure.onClose}
          onConfirm={handleConfirmDelete}
          open={deleteDisclosure.isOpen}
          title="Confirm delete"
          content={<p>Are you sure you wish to delete these fees?</p>}
          confirmBtnProps={{ disabled: deleteOneOffFeesBatch.isPending }}
          cancelBtnProps={{ variant: 'outlined', color: 'primary' }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default OneOffFeesAndDiscounts;
